import { QuickActionZod } from "@redotech/redo-customer-widget-common/quick-action";
import { FaqActionButtonSchema } from "@redotech/redo-support-common/faq/faq-body-schema";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ISODateString, TimeString } from "../schema-utils";
import type { SupportFlatTeamSettingKey } from "./catalog";
import {
  SUPPORT_CLOSE_ACTION_OPTIONS,
  SUPPORT_CONTACT_FORM_FONT_OPTIONS,
  SUPPORT_CONTACT_FORM_REPLY_EMAIL_INTEGRATION_KIND_OPTIONS,
  SUPPORT_CONTACT_FORM_SUBJECT_INPUT_TYPE_OPTIONS,
  SUPPORT_CSAT_SURVEY_METHOD_OPTIONS,
  SUPPORT_CSAT_SURVEY_SEND_TIME_OPTIONS,
  SUPPORT_DEFAULT_SEND_ACTION_OPTIONS,
  SUPPORT_OPENAI_MODEL_OPTIONS,
  SUPPORT_TICKET_ASSIGNMENT_OPTIONS,
  SUPPORT_TICKET_HANDOFF_TYPE_OPTIONS,
  SUPPORT_VOICE_MESSAGE_TYPE_OPTIONS,
  SUPPORT_VOICE_PROVIDER_OPTIONS,
  SUPPORT_VOICE_RINGING_STRATEGY_OPTIONS,
  supportSettingOptionValues,
} from "./selection-options";

const TicketHandoffType = createEnum(
  supportSettingOptionValues(SUPPORT_TICKET_HANDOFF_TYPE_OPTIONS),
);
const PillTheme = z.string();
const SenderEmailSetupStatus = createEnum(["connected", "unverified"]);
const SupportDefaultSendAction = createEnum(
  supportSettingOptionValues(SUPPORT_DEFAULT_SEND_ACTION_OPTIONS),
);
const SupportCloseAction = createEnum(
  supportSettingOptionValues(SUPPORT_CLOSE_ACTION_OPTIONS),
);
const SupportTicketAssignment = createEnum(
  supportSettingOptionValues(SUPPORT_TICKET_ASSIGNMENT_OPTIONS),
);
const SupportOpenAiModel = createEnum(
  supportSettingOptionValues(SUPPORT_OPENAI_MODEL_OPTIONS),
);
const SupportVoiceProvider = createEnum(
  supportSettingOptionValues(SUPPORT_VOICE_PROVIDER_OPTIONS),
);
const SupportVoiceRingingStrategy = createEnum(
  supportSettingOptionValues(SUPPORT_VOICE_RINGING_STRATEGY_OPTIONS),
);
const SupportVoiceMessageType = createEnum(
  supportSettingOptionValues(SUPPORT_VOICE_MESSAGE_TYPE_OPTIONS),
);
const SupportContactFormSubjectInputType = createEnum(
  supportSettingOptionValues(SUPPORT_CONTACT_FORM_SUBJECT_INPUT_TYPE_OPTIONS),
);
const SupportContactFormReplyEmailIntegrationKind = createEnum(
  supportSettingOptionValues(
    SUPPORT_CONTACT_FORM_REPLY_EMAIL_INTEGRATION_KIND_OPTIONS,
  ),
);
const SupportContactFormFont = createEnum(
  supportSettingOptionValues(SUPPORT_CONTACT_FORM_FONT_OPTIONS),
);
const SupportCsatSurveySendTime = createEnum(
  supportSettingOptionValues(SUPPORT_CSAT_SURVEY_SEND_TIME_OPTIONS),
);
const SupportCsatSurveyMethod = createEnum(
  supportSettingOptionValues(SUPPORT_CSAT_SURVEY_METHOD_OPTIONS),
);
const SenderEmailSchema = z.object({
  email: z.email(),
  emailSignature: z.string().nullish(),
  name: z.string(),
  postmarkId: z.coerce.number().int(),
  domainId: z.coerce.number().int(),
  dkimHost: z.string(),
  dkimText: z.string(),
  returnPathDomain: z.string(),
  returnPathValue: z.string(),
  setupStatus: SenderEmailSetupStatus,
});

const DailyScheduleSchema = z
  .object({
    enabled: z.boolean().nullish(),
    start: TimeString.nullish(),
    end: TimeString.nullish(),
  })
  .nullish();

const WidgetTimeBlocksSchema = z
  .array(z.object({ start: TimeString.nullish(), end: TimeString.nullish() }))
  .nullish();

const SupportFlatSettingShape = {
  "support/enabled": z.boolean().nullish(),
  "support/defaultEmail": z.email().nullish(),
  "support/defaultSendAction": SupportDefaultSendAction.nullish(),
  "support/emailSignature": z.string().nullish(),
  "support/excludedEmails": z.array(z.string().nullable()).nullish(),
  "support/senderEmails": z.array(SenderEmailSchema).nullish(),
  "support/tagSpam": z.boolean().nullish(),
  "support/closeAction": SupportCloseAction.nullish(),
  "support/undoSend": z.boolean().nullish(),
  "support/showCreatedDateColumn": z.boolean().nullish(),
  "support/allowUnifiedInbox": z.boolean().nullish(),
  "support/tags": z
    .array(z.object({ name: z.string(), pillTheme: PillTheme }))
    .nullish(),
  "support/autoMergeExclusions": z.array(z.string().nullable()).nullish(),
  "support/autoSplitSenders": z.array(z.string().nullable()).nullish(),
  "support/maxTicketsPerUser": z.coerce.number().nullish(),
  "support/ticketAssignment": SupportTicketAssignment.nullish(),
  "support/usersForAutoAssignment": z.array(zExt.objectId()).nullish(),
  "support/autoAssignOnResponse": z.boolean().nullish(),
  "support/autoReassignOnResponse": z.boolean().nullish(),
  "support/autoReassignFromAiOnResponse": z.boolean().nullish(),
  "support/autoReassignFromHumanOnResponse": z.boolean().nullish(),
  "support/unassignOpenTicketsAtShiftEnd": z.boolean().nullish(),
  "support/keepAssigneeOnTicketReopen": z.boolean().nullish(),
  "support/useInProgressStatus": z.boolean().nullish(),
  "support/autoMergeEnabled": z.boolean().nullish(),
  "support/propagateDeletes": z.boolean().nullish(),
  "support/closeTicketsOnGmailArchive": z.boolean().nullish(),
  "support/autoCloseArchivedGmailMessages": z.boolean().nullish(),
  "support/aiEmailSignature": z.string().nullish(),
  "support/ai/enabled": z.boolean().nullish(),
  "support/ai/respondToInternalEmails": z.boolean().nullish(),
  "support/ai/copilotEnabled": z.boolean().nullish(),
  "support/ai/copilotModel": SupportOpenAiModel.nullish(),
  "support/ai/autoGenerateResponses": z.boolean().nullish(),
  "support/ai/useEmailSignature": z.boolean().nullish(),
  "support/ai/autopilotEnabled": z.boolean().nullish(),
  "support/ai/automateLiveChat": z.boolean().nullish(),
  "support/ai/autoCloseNoReplyTickets": z.boolean().nullish(),
  "support/ai/aiAutopilotTicketVolume": z.number().nullish(),
  "support/ai/trialMessagesRemaining": z.number().int().min(0).nullish(),
  "support/ai/hasTriedAi": z.boolean().nullish(),
  "support/ai/whitelistedDomains": z.array(z.string()).nullish(),
  "support/ai/whitelistedEmails": z.array(z.string()).nullish(),
  "support/ai/botGreeting": z.string().nullish(),
  "support/ai/humanHandoffMessage": z.string().nullish(),
  "support/ai/emailVerificationRequired": z.boolean().nullish(),
  "support/ai/ticketDescription": z.string().nullish(),
  "support/ai/disallowedPrompts": z.array(z.string()).nullish(),
  "support/ai/disallowedPromptsEnabled": z.boolean().nullish(),
  "support/ai/autopilotToolSettings/cancelOrder/cancelOrderTimeWindowAfterPlacingMinutes":
    z.number().int().min(0).nullish(),
  "support/ai/autopilotToolSettings/updateOrderAddress/updateOrderAddressTimeWindowAfterPlacingMinutes":
    z.number().int().min(0).nullish(),
  "support/ai/enabledPlatforms/email": z.boolean().nullish(),
  "support/ai/enabledPlatforms/facebook": z.boolean().nullish(),
  "support/ai/enabledPlatforms/facebookComments": z.boolean().nullish(),
  "support/ai/enabledPlatforms/instagram": z.boolean().nullish(),
  "support/ai/enabledPlatforms/instagramComments": z.boolean().nullish(),
  "support/ai/enabledPlatforms/sms": z.boolean().nullish(),
  "support/ai/enabledPlatforms/tiktok": z.boolean().nullish(),
  "support/ai/enabledPlatforms/attentive": z.boolean().nullish(),
  "support/ai/enabledPlatforms/postscript": z.boolean().nullish(),
  "support/billing/base/pricePerMonthInCents": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/base/subscriptionActive": z.boolean().nullish(),
  "support/billing/base/createdAt": ISODateString.nullish(),
  "support/billing/base/updatedAt": ISODateString.nullish(),
  "support/billing/base/billingStartDate": ISODateString.nullish(),
  "support/billing/base/freeTrialEndDate": ISODateString.nullish(),
  "support/billing/base/version": z.string().nullish(),
  "support/billing/ai/aiResolutionPriceInCents": z.number().min(0).nullish(),
  "support/billing/ai/atShopifyCap": z.boolean().nullish(),
  "support/billing/ai/billingAccepted": z.boolean().nullish(),
  "support/billing/ai/billingStartDate": ISODateString.nullish(),
  "support/billing/ai/freeTrialDays": z.number().int().min(0).nullish(),
  "support/billing/ai/freeTrialEndDate": ISODateString.nullish(),
  "support/billing/ai/freeTrialUsed": z.boolean().nullish(),
  "support/billing/ai/ticketsUsed": z.number().int().min(0).nullish(),
  "support/billing/ai/createdAt": ISODateString.nullish(),
  "support/billing/ai/updatedAt": ISODateString.nullish(),
  "support/billing/ai/pricePerMonth": z.number().min(0).nullish(),
  "support/billing/ai/ticketsCovered": z.number().int().min(0).nullish(),
  "support/billing/textMessaging/enabled": z.boolean().nullish(),
  "support/billing/textMessaging/billingFailed": z.boolean().nullish(),
  "support/billing/textMessaging/subscriptionActive": z.boolean().nullish(),
  "support/billing/textMessaging/monthlyMinimumInTenthCents": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/textMessaging/pricePerSMSInTenthCents": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/textMessaging/pricePerMMSInTenthCents": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/textMessaging/previousCycles": z.array(z.any()).nullish(),
  "support/billing/textMessaging/currentBillingCycle/billingCycleStartDate":
    ISODateString.nullish(),
  "support/billing/textMessaging/currentBillingCycle/lastBilledAt":
    ISODateString.nullish(),
  "support/billing/textMessaging/currentBillingCycle/monthlyMinimumRemainingInTenthCents":
    z.number().int().min(0).nullish(),
  "support/billing/textMessaging/currentBillingCycle/billed/smsMessagesReceived":
    z.number().int().min(0).nullish(),
  "support/billing/textMessaging/currentBillingCycle/billed/smsMessagesSent": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/textMessaging/currentBillingCycle/billed/mmsMessagesReceived":
    z.number().int().min(0).nullish(),
  "support/billing/textMessaging/currentBillingCycle/billed/mmsMessagesSent": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/textMessaging/unbilled/smsMessagesReceived": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/textMessaging/unbilled/smsMessagesSent": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/textMessaging/unbilled/mmsMessagesReceived": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/textMessaging/unbilled/mmsMessagesSent": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/voice/billingStartDate": ISODateString.nullish(),
  "support/billing/voice/createdAt": ISODateString.nullish(),
  "support/billing/voice/updatedAt": ISODateString.nullish(),
  "support/billing/voice/pricePerMinuteInCents": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/voice/pricePerMonthInCents": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "support/billing/voice/subscriptionActive": z.boolean().nullish(),
  "support/voice/enabled": z.boolean().nullish(),
  "support/voice/provider": SupportVoiceProvider.nullish(),
  "support/voice/availableHoursEnabled": z.boolean().nullish(),
  "support/voice/availableHoursTimezone": z.string().nullish(),
  "support/voice/ringingStrategy": SupportVoiceRingingStrategy.nullish(),
  "support/voice/allowHoldingCallsInQueue": z.boolean().nullish(),
  "support/voice/sendToVoicemailWhenAllAgentsAreOffline": z.boolean().nullish(),
  "support/voice/playAutomatedMessageWhenAllAgentsAreOffline": z
    .boolean()
    .nullish(),
  "support/voice/holdMusicName": z.string().nullish(),
  "support/voice/holdMusicUrl": z.string().nullish(),
  "support/voice/billingGroupId": z.string().nullish(),
  "support/voice/sipConnectionId": z.string().nullish(),
  "support/voice/sipConnectionCredential": z.string().nullish(),
  "support/voice/voiceApplicationId": z.string().nullish(),
  "support/voice/outboundVoiceProfileId": z.string().nullish(),
  "support/voice/availableHours/monday": DailyScheduleSchema,
  "support/voice/availableHours/tuesday": DailyScheduleSchema,
  "support/voice/availableHours/wednesday": DailyScheduleSchema,
  "support/voice/availableHours/thursday": DailyScheduleSchema,
  "support/voice/availableHours/friday": DailyScheduleSchema,
  "support/voice/availableHours/saturday": DailyScheduleSchema,
  "support/voice/availableHours/sunday": DailyScheduleSchema,
  "support/voice/inboundGreetingMessage/enabled": z.boolean().nullish(),
  "support/voice/inboundGreetingMessage/type":
    SupportVoiceMessageType.nullish(),
  "support/voice/inboundGreetingMessage/ttsMessage": z.string().nullish(),
  "support/voice/inboundGreetingMessage/audioName": z.string().nullish(),
  "support/voice/inboundGreetingMessage/audioUrl": z.string().nullish(),
  "support/voice/outboundGreetingMessage/enabled": z.boolean().nullish(),
  "support/voice/outboundGreetingMessage/type":
    SupportVoiceMessageType.nullish(),
  "support/voice/outboundGreetingMessage/ttsMessage": z.string().nullish(),
  "support/voice/outboundGreetingMessage/audioName": z.string().nullish(),
  "support/voice/outboundGreetingMessage/audioUrl": z.string().nullish(),
  "support/voice/offHoursMessage/enabled": z.boolean().nullish(),
  "support/voice/offHoursMessage/type": SupportVoiceMessageType.nullish(),
  "support/voice/offHoursMessage/ttsMessage": z.string().nullish(),
  "support/voice/offHoursMessage/audioName": z.string().nullish(),
  "support/voice/offHoursMessage/audioUrl": z.string().nullish(),
  "support/voice/voicemailMessage/enabled": z.boolean().nullish(),
  "support/voice/voicemailMessage/type": SupportVoiceMessageType.nullish(),
  "support/voice/voicemailMessage/ttsMessage": z.string().nullish(),
  "support/voice/voicemailMessage/audioName": z.string().nullish(),
  "support/voice/voicemailMessage/audioUrl": z.string().nullish(),
  "support/contactForm/enabled": z.boolean().nullish(),
  "support/contactForm/title": z.string().nullish(),
  "support/contactForm/successMessage": z.string().nullish(),
  "support/contactForm/customCss": z.string().nullish(),
  "support/contactForm/allowFileUpload": z.boolean().nullish(),
  "support/contactForm/customFields": z.array(z.any()).nullish(),
  "support/contactForm/font": SupportContactFormFont.nullish(),
  "support/contactForm/collapsibleForm": z.boolean().nullish(),
  "support/contactForm/showActions/quickActions": z.boolean().nullish(),
  "support/contactForm/showActions/orderEditing": z.boolean().nullish(),
  "support/contactForm/showActions/returnPortal": z.boolean().nullish(),
  "support/contactForm/showActions/faq": z.boolean().nullish(),
  "support/contactForm/submitButton/label": z.string().nullish(),
  "support/contactForm/submitButton/color": z.string().nullish(),
  "support/contactForm/name/placeholder": z.string().nullish(),
  "support/contactForm/name/required": z.boolean().nullish(),
  "support/contactForm/name/show": z.boolean().nullish(),
  "support/contactForm/email/placeholder": z.string().nullish(),
  "support/contactForm/phone/placeholder": z.string().nullish(),
  "support/contactForm/phone/required": z.boolean().nullish(),
  "support/contactForm/phone/show": z.boolean().nullish(),
  "support/contactForm/subject/placeholder": z.string().nullish(),
  "support/contactForm/subject/required": z.boolean().nullish(),
  "support/contactForm/subject/show": z.boolean().nullish(),
  "support/contactForm/subject/options": z.array(z.string()).nullish(),
  "support/contactForm/subject/inputType":
    SupportContactFormSubjectInputType.nullish(),
  "support/contactForm/comment/placeholder": z.string().nullish(),
  "support/contactForm/replyEmail/email": z.email().nullish(),
  "support/contactForm/replyEmail/name": z.string().nullish(),
  "support/contactForm/replyEmail/integrationKind":
    SupportContactFormReplyEmailIntegrationKind.nullish(),
  "customerWidget/general/widgetQuickActions": z
    .array(QuickActionZod)
    .nullish(),
  "support/csat/enabled": z.boolean().nullish(),
  "support/csat/allowRemarks": z.boolean().nullish(),
  "support/csat/surveyMessage": z.string().nullish(),
  "support/csat/surveySendTime": SupportCsatSurveySendTime.nullish(),
  "support/csat/surveyMethods": z.array(SupportCsatSurveyMethod).nullish(),
  "support/csat/headerImage": z.string().nullish(),
  "support/csat/sampleRate": z.number().int().min(0).max(100).nullish(),
  "support/csat/allowedInboxEmails": z.array(z.string()).nullish(),
  "support/csat/emailTemplateId": z.string().nullish(),
  "support/csat/abTestEnabled": z.boolean().nullish(),
  "support/csat/abTestVariants": z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        weight: z.number().int().min(0),
        surveySendTime: SupportCsatSurveySendTime.nullish(),
        emailTemplateId: z.string().nullish(),
      }),
    )
    .nullish(),
  "support/facebook/autoCloseExternal": z.boolean().nullish(),
  "support/facebook/commentsEnabled": z.boolean().nullish(),
  "support/instagram/autoCloseExternal": z.boolean().nullish(),
  "support/instagram/commentsEnabled": z.boolean().nullish(),
  "support/instagram/autoCloseMentions": z.boolean().nullish(),
  "support/instagram/autoCloseRepliedStories": z.boolean().nullish(),
  "support/metaPagesData/pagesData": z.array(z.any()).nullish(),
  "support/sms/enabled": z.boolean().nullish(),
  "support/sms/privacyPolicyUrl": z.string().nullish(),
  "support/sms/termsAndConditionsUrl": z.string().nullish(),
  "support/faq/actionButtons": z.array(FaqActionButtonSchema).nullish(),
  "support/faq/brandKitOverrides/pageBackgroundColor": z.boolean().nullish(),
  "support/faq/collectionBackgroundColor": z.string().nullish(),
  "support/faq/collectionBorderColor": z.string().nullish(),
  "support/faq/collectionCornerRadius": z.number().min(0).nullish(),
  "support/faq/displayCollectionIcon": z.boolean().nullish(),
  "support/faq/displayCollectionSubtext": z.boolean().nullish(),
  "support/faq/headlineText": z.string().nullish(),
  "support/faq/headlineTextColor": z.string().nullish(),
  "support/faq/homeBackgroundImageUrl": z.string().nullish(),
  "support/faq/overlayColor": z.string().nullish(),
  "support/faq/overlayOpacity": z.number().nullish(),
  "support/faq/pageBackgroundColor": z.string().nullish(),
  "customerWidget/support/chatExpirationMessage": z.string().nullish(),
  "customerWidget/support/chatExpirationMinutes": z
    .number()
    .int()
    .min(1)
    .nullish(),
  "customerWidget/support/emailConversionPrepMessage": z.string().nullish(),
  "customerWidget/support/liveChatConversionPrepMessage": z.string().nullish(),
  "customerWidget/support/hideSupportWidgetFeatures": z.boolean().nullish(),
  "customerWidget/support/messagingHeadingText": z.string().nullish(),
  "customerWidget/support/unresponsiveConversionEnabled": z.boolean().nullish(),
  "customerWidget/support/unresponsiveConversionMinutes": z
    .number()
    .int()
    .min(0)
    .nullish(),
  "customerWidget/support/unresponsiveConversionSeconds": z
    .number()
    .int()
    .min(0)
    .max(59)
    .nullish(),
  "customerWidget/general/hideWidgetAfterHours": z.boolean().nullish(),
  "customerWidget/general/widgetHoursEnabled": z.boolean().nullish(),
  "customerWidget/general/widgetHoursTimezone": z.string().nullish(),
  "customerWidget/general/widgetHours/monday/enabled": z.boolean().nullish(),
  "customerWidget/general/widgetHours/monday/timeBlocks":
    WidgetTimeBlocksSchema,
  "customerWidget/general/widgetHours/tuesday/enabled": z.boolean().nullish(),
  "customerWidget/general/widgetHours/tuesday/timeBlocks":
    WidgetTimeBlocksSchema,
  "customerWidget/general/widgetHours/wednesday/enabled": z.boolean().nullish(),
  "customerWidget/general/widgetHours/wednesday/timeBlocks":
    WidgetTimeBlocksSchema,
  "customerWidget/general/widgetHours/thursday/enabled": z.boolean().nullish(),
  "customerWidget/general/widgetHours/thursday/timeBlocks":
    WidgetTimeBlocksSchema,
  "customerWidget/general/widgetHours/friday/enabled": z.boolean().nullish(),
  "customerWidget/general/widgetHours/friday/timeBlocks":
    WidgetTimeBlocksSchema,
  "customerWidget/general/widgetHours/saturday/enabled": z.boolean().nullish(),
  "customerWidget/general/widgetHours/saturday/timeBlocks":
    WidgetTimeBlocksSchema,
  "customerWidget/general/widgetHours/sunday/enabled": z.boolean().nullish(),
  "customerWidget/general/widgetHours/sunday/timeBlocks":
    WidgetTimeBlocksSchema,
  "concierge/ticketHandoffSettings/handoffType": TicketHandoffType.nullish(),
  "concierge/ticketHandoffSettings/emailHandoffMessage": z.string().nullish(),
  "concierge/ticketHandoffSettings/collectCustomerEmailBeforeHandoff": z
    .boolean()
    .nullish(),
  "concierge/ticketHandoffSettings/forwardToExternalEmail": z
    .boolean()
    .nullish(),
} as const satisfies Record<SupportFlatTeamSettingKey, z.ZodTypeAny>;

export const SupportFlatTeamSettingsSchema = z.object(SupportFlatSettingShape);
